<template>
  <section v-if="hasRecommendations" class="careerRecommendations">
    <span class="careerRecommendations__title">{{ t('careerGuide.recommendationsTitle') }}</span>
    <p class="careerRecommendations__description">{{ t('careerGuide.recommendationsDescription') }}</p>

    <div class="careerRecommendations__tabs" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="careerRecommendations__tab"
        :class="{ careerRecommendations__tab_active: activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        {{ tab.text }}
      </button>
    </div>

    <div class="careerRecommendations__list">
      <div
        v-for="(item, index) in items"
        :key="`${activeTab}-${index}`"
        class="careerRecommendations__item"
        :class="`careerRecommendations__item_${activeTab}`"
      >
        <template v-if="activeTab === 'professions'">
          <div class="careerRecommendations__itemInfo">
            <span class="careerRecommendations__name">{{ getLocalizedText(item.name) }}</span>
            <span v-if="getBuckets(item).length" class="careerRecommendations__subtitle">{{ getBuckets(item).join(', ') }}</span>
          </div>
          <div class="careerRecommendations__score">{{ getSimilarity(item) }}%</div>
        </template>

        <template v-else>
          <img v-if="item.logo" :src="item.logo" alt="" class="careerRecommendations__logo" />
          <div v-else class="careerRecommendations__logo" :class="`careerRecommendations__logo_${activeTab}`">
            {{ getInitial(item) }}
          </div>

          <div class="careerRecommendations__itemInfo">
            <div v-if="getCityName(item)" class="careerRecommendations__city">
              <span class="material-icons careerRecommendations__location">location_on</span>
              <span>{{ getCityName(item) }}</span>
            </div>
            <span class="careerRecommendations__name">{{ getLocalizedText(item.name) }}</span>
            <a v-if="getFullstackLink(item)" class="careerRecommendations__link" :href="getFullstackLink(item)" target="_blank">
              {{ activeTab === 'enterprises' ? t('careerGuide.detailsLink') : t('careerGuide.siteLink') }}
            </a>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  recommendations: {
    type: Object,
    default: null
  }
});

const { t, locale } = useI18n();
const activeTab = ref('professions');

const recommendationGroups = computed(() => ({
  professions: getArray(props.recommendations?.professions),
  education: getArray(props.recommendations?.institutions || props.recommendations?.education),
  enterprises: getArray(props.recommendations?.firms || props.recommendations?.enterprises)
}));

const tabs = computed(() =>
  [
    { key: 'professions', text: t('careerGuide.professionsTab') },
    { key: 'education', text: t('careerGuide.educationTab') },
    { key: 'enterprises', text: t('careerGuide.enterprisesTab') }
  ].filter((tab) => recommendationGroups.value[tab.key]?.length)
);

const items = computed(() => recommendationGroups.value[activeTab.value] || []);
const hasRecommendations = computed(() => tabs.value.length > 0);

watch(
  tabs,
  (nextTabs) => {
    if (nextTabs.length && !recommendationGroups.value[activeTab.value]?.length) {
      activeTab.value = nextTabs[0].key;
    }
  },
  { immediate: true }
);

function getLocalizedText(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return '';
  if (locale.value === 'kz') return value.kk || value.kz || value.ru || '';
  return value.ru || value.kk || value.kz || '';
}

function getArray(value) {
  return Array.isArray(value) ? value : [];
}

function getBuckets(item) {
  return Array.isArray(item?.buckets) ? item.buckets.map(getLocalizedText).filter(Boolean) : [];
}

function getCityName(item) {
  return getLocalizedText(item?.city_name || item?.city?.name_current || item?.city?.name);
}

function getFullstackLink(item) {
  return item?._links?.fullstack?.href || item?._links?.fullstack || '';
}

function getSimilarity(item) {
  return Math.round(Number(item?.similarity) || 0);
}

function getInitial(item) {
  return getLocalizedText(item?.name).charAt(0).toUpperCase();
}
</script>

<style scoped>
.careerRecommendations {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 16px;
  padding-bottom: 16px;
}

.careerRecommendations__title {
  font-family: Roboto, sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  color: #242930;
}

.careerRecommendations__description {
  max-width: 720px;
  margin: 0;
  font-family: Roboto, sans-serif;
  font-size: 18px;
  line-height: 30px;
  color: #242930;
}

.careerRecommendations__tabs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding: 2px 2px 8px;
  scrollbar-width: none;
}

.careerRecommendations__tabs::-webkit-scrollbar {
  display: none;
}

.careerRecommendations__tab {
  min-width: 72px;
  height: 32px;
  border: 0;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(27, 36, 44, 0.12);
  padding: 0 12px;
  font-family: Roboto, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  color: #242930;
  white-space: nowrap;
  cursor: pointer;
}

.careerRecommendations__tab_active {
  color: #003399;
  box-shadow: 0 2px 8px rgba(47, 83, 151, 0.2);
}

.careerRecommendations__list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 24px;
}

.careerRecommendations__item {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 18px;
  align-items: center;
  min-width: 0;
  border-bottom: 1px solid #f0f1f3;
  padding: 12px 0;
}

.careerRecommendations__item_professions {
  grid-template-columns: minmax(0, 1fr) 44px;
  gap: 12px;
}

.careerRecommendations__item_enterprises {
  grid-template-columns: 72px minmax(0, 1fr);
}

.careerRecommendations__item_enterprises .careerRecommendations__logo {
  width: 56px;
  height: 56px;
  font-size: 14px;
}

.careerRecommendations__logo {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ebecee;
  background: #f1f7ff;
  font-family: Roboto, sans-serif;
  font-weight: 800;
  font-size: 24px;
  color: #163e64;
  object-fit: contain;
}

.careerRecommendations__logo_enterprises {
  background: #f4faff;
}

.careerRecommendations__itemInfo {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.careerRecommendations__city {
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #626c70;
}

.careerRecommendations__location {
  font-size: 14px;
  line-height: 1;
  color: #626c70;
}

.careerRecommendations__name {
  font-family: Roboto, sans-serif;
  font-weight: 700;
  font-size: 15px;
  line-height: 20px;
  color: #1b1c1c;
  overflow-wrap: anywhere;
}

.careerRecommendations__subtitle {
  font-family: Roboto, sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: #737982;
  overflow-wrap: anywhere;
}

.careerRecommendations__score {
  width: 36px;
  height: 36px;
  border: 3px solid #4caf50;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Roboto, sans-serif;
  font-weight: 500;
  font-size: 11px;
  line-height: 1;
  color: #4caf50;
}

.careerRecommendations__link {
  width: fit-content;
  border: 0;
  background: transparent;
  padding: 0;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #2196f3;
  cursor: pointer;
  text-decoration: none;
}

.careerRecommendations__link:hover {
  color: #0b75c9;
  text-decoration: underline;
}

@media screen and (min-width: 769px) {
  .careerRecommendations {
    max-width: 920px;
  }
}

@media screen and (max-width: 768px) {
  .careerRecommendations__title {
    font-size: 20px;
    line-height: 28px;
  }

  .careerRecommendations__description {
    font-size: 16px;
    line-height: 22px;
  }

  .careerRecommendations__list {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .careerRecommendations__item {
    grid-template-columns: 104px minmax(0, 1fr);
    gap: 12px;
  }

  .careerRecommendations__item_professions {
    grid-template-columns: minmax(0, 1fr) 44px;
  }

  .careerRecommendations__item_enterprises {
    grid-template-columns: 56px minmax(0, 1fr);
  }

  .careerRecommendations__logo {
    width: 104px;
    height: 84px;
    border-radius: 6px;
  }
}
</style>
